import type {
  LicenceDetail,
  LicenceResponse,
  LicenceStatsResponse,
  UserResponse,
} from '@shared/schemas';

import api from '@/shared/api/api-client';

import { toCsmCandidates } from '../../_shared/domain/licence-csm';
import { clientCategoriesHttp } from '../../_shared/infrastructure/client-categories.http';
import type { LicencesGateway } from './licences.gateway';

/**
 * Real implementation over the shared api client. The single place that knows
 * the Licences index endpoints: the two reads kept 1:1 with the original
 * `Licences` page's fetches, the Danger zone's two writes, and the licence
 * page's Edit licence calls.
 */
export const httpLicencesGateway: LicencesGateway = {
  fetchLicences: () => api.get<LicenceResponse[]>('/licences'),
  fetchStats: () => api.get<LicenceStatsResponse>('/licences/stats'),
  // The response also carries the licence as it now stands; the index patches
  // its row itself and keeps only the count the success message names.
  setLicenceStatus: async ({ licenceId, status }) => {
    const data = await api.patch<{ revoked_sessions: number }>(
      `/admin/licences/${licenceId}/status`,
      { status },
    );
    return { revokedSessions: data.revoked_sessions };
  },
  deleteLicence: async (licenceId) => {
    await api.delete(`/admin/licences/${licenceId}`);
  },
  loadLicence: (licenceId) => api.get<LicenceDetail>(`/licences/${licenceId}`),
  // The contract (#750) is always sent too: an emptied field clears it.
  updateLicence: ({
    licenceId,
    name,
    client_category,
    based_in,
    csm_user_id,
    contract_start_date,
    annual_profiles_target,
  }) =>
    api.patch<LicenceDetail>(`/licences/${licenceId}`, {
      name: name.trim(),
      client_category,
      based_in,
      csm_user_id,
      contract_start_date,
      annual_profiles_target,
    }),
  loadCsmCandidates: async () =>
    toCsmCandidates(await api.get<UserResponse[]>('/users?role=admin')),
  ...clientCategoriesHttp,
};
